import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  View,
  ScrollView,
  Image,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Modal,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import * as authActions from '../store/actions/auth';
import InputForm from '../components/InputForm';
import { CommonProperties, CommonStyles, Color } from '../styles';
import Button, { BUTTON_TYPES } from '../components/Button';
import { openAlert } from '../store/actions/alert';
import { useTranslation } from 'react-i18next';
import BaseText from '../components/BaseText';
import { useComponentTKey, i18nNS } from '../i18n';
import * as SecureStore from 'expo-secure-store';
import * as LocalAuthentication from 'expo-local-authentication';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { EXPENSEPOINT_ENABLED, REBOOK_ENABLED, CREW_ENABLED, ssoUrl } from '../config/env';
import { userAuthenticate } from '../store/actions/auth';
import { getSSOData } from '../utils/auth.utils';
import { logException } from '../utils/logger.utils';
import ForgotPassword from '../components/modals/ForgotPassword';
import { useActivationLink } from '../utils/useActivationLink';
import { useNavigation } from '@react-navigation/native';
import { Screens } from '../constants/ScreenNames';

const LoginScreen = () => {
  const navigation = useNavigation();
  const { t } = useTranslation(i18nNS.login);
  const { tKey } = useComponentTKey('LoginScreen');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const isLoading = useSelector((state) => state.auth.loginLoading);
  const dispatch = useDispatch();
  const sso = useSelector((state) => state.auth.isSso);
  const [isSso, setSso] = useState(sso);
  const [errorMessages, setErrorMessages] = useState({});
  const [bioData, setBioData] = useState({ user: null, password: null, available: null });
  const [forgotPswd, setForgotPswd] = useState(false);
  const activationValue = useActivationLink();

  const isSafariBrowser = () => {
    return Platform.OS === 'web' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  useLayoutEffect(() => {
    if (activationValue) {
      navigation.navigate(Screens.UserActivation, { value: activationValue });
    }
  }, [activationValue, navigation]);

  const validateEmail = (email, t) => {
    if (!email) {
      return t('common:Error.requiredEmail');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('common:Error.invalidEmail');
    }
    return null; // No error
  };

  const isValid = () => {
    const _errorMessages = {};
    const emailError = validateEmail(email, t);
    if (emailError) {
      _errorMessages.email = emailError;
    }
    if (!password) {
      _errorMessages.password = t('common:Error.requiredPassword');
    }
    setErrorMessages(_errorMessages);
    return Object.keys(_errorMessages).length === 0;
  };

  const onPressLogin = async () => {
    if (!isValid()) {
      return;
    }

    const userData = await dispatch(
      authActions.userAuthenticate({
        userName: email,
        password,
        storeEmail: bioData.user,
        storePassword: bioData.password,
        bioAvailability: bioData.available,
      })
    );
    if (userData?.error) {
      setErrorMessages((prev) => ({
        ...prev,
        password: userData.message, // Display the error in the password field
      }));
      return;
    }
  };

  const handleSSOAuthentication = async (companyCode) => {
    try {
      const result = await WebBrowser.openAuthSessionAsync(
        `${ssoUrl}${companyCode}?skipSelfEnrollCompany=true`
      );
      if (result?.url) {
        const ssoData = getSSOData(result.url);
        if (ssoData?.isValid) {
          dispatch(
            userAuthenticate({
              userName: ssoData.uuid,
              password: ssoData.password,
              isSso: true,
            })
          );
        }
      }
    } catch (err) {
      console.warn('SSO authentication error:', err);
      dispatch(
        openAlert({
          title: t('Error'),
          message: t('Unable to complete authentication. Please try again.'),
        })
      );
    }
  };

  const onPressSso = async () => {
    const _errorMessages = {};
    const emailError = validateEmail(email, t);
    if (emailError) {
      _errorMessages.email = emailError;
      setErrorMessages(_errorMessages);
      return;
    }

    const user = await dispatch(authActions.ssoAuthenticate(email, password));
    if (user?.companyCode) {
      addURLEventListener();

      if (isSafariBrowser()) {
        dispatch(
          openAlert({
            title: t('SSO Authentication'),
            message: t('Please continue to authenticate with your organization'),
            confirmText: 'Allow',
            cancelText: t('Cancel'),
            onConfirmPressed: () => handleSSOAuthentication(user.companyCode),
          })
        );
      } else {
        handleSSOAuthentication(user.companyCode);
      }

      setSso(true);
    } else {
      setSso(false);
    }
    if (
      user?.UserInformation != null &&
      user?.UserInformation != '' &&
      user?.UserInformation != undefined
    ) {
      dispatch(openAlert({ title: user.UserInformation }));
    }
  };

  const handleRedirect = async (event) => {
    if (Platform.OS === 'ios') {
      WebBrowser.dismissBrowser();
    } else {
      removeURLEventListener();
    }
    if (event?.url) {
      if (Platform.OS === 'web') {
        dispatch(authActions.userAuthenticateReload());
        return;
      }

      const ssoData = getSSOData(event.url);
      if (ssoData.isValid) {
        dispatch(
          userAuthenticate({
            userName: ssoData.uuid,
            password: ssoData.password,
            isSso: true,
          })
        );
      }
    }
  };

  let subscription = null;
  const addURLEventListener = () => {
    subscription = Linking.addEventListener('url', handleRedirect);
  };
  const removeURLEventListener = () => {
    subscription.remove();
  };

  useEffect(() => {
    const getBioAvailability = async () => {
      try {
        const user = await SecureStore.getItemAsync('userName');
        const password = await SecureStore.getItemAsync('password');

        const isLocalAuthAvailable = await LocalAuthentication.hasHardwareAsync();
        if (isLocalAuthAvailable) {
          const isEnrolled = await LocalAuthentication.isEnrolledAsync();
          if (isEnrolled) {
            const authTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
            if (authTypes.length > 0) {
              const available = await LocalAuthentication.isEnrolledAsync();
              if (available) {
                setBioData({
                  user,
                  password,
                  available,
                });
              }
            }
          }
        }
      } catch (error) {
        console.warn('Error trying to get user and pass locally ', error);
        logException(error, { metaData: { error } });
      }
    };

    if (Platform.OS !== 'web') {
      getBioAvailability();
    }
  }, []);

  useEffect(() => {
    const authenticateLocally = async () => {
      try {
        const result = await LocalAuthentication.authenticateAsync({
          promptMessage: t(tKey('LoginBio')),
        });

        if (result.success) {
          dispatch(
            authActions.userAuthenticate({
              userName: bioData.user,
              password: bioData.password,
              storeEmail: bioData.user,
              storePassword: bioData.password,
              bioAvailability: bioData.available,
            })
          );
        } else {
          console.warn(result);
        }
      } catch (error) {
        console.log('Error trying to auth locally ', error);
      }
    };

    if (bioData.user && bioData.password && bioData.available) {
      authenticateLocally();
    }
  }, [bioData, dispatch, tKey, t]);

  return (
    <ScrollView keyboardShouldPersistTaps="always">
      <View style={[styles.body, REBOOK_ENABLED ? { marginTop: 0, marginBottom: 0 } : null]}>
        <>
          <Image
            style={CREW_ENABLED ? styles.crewLogo : styles.logo}
            source={
              EXPENSEPOINT_ENABLED
                ? require('../assets/img/logo-expensepoint.png')
                : CREW_ENABLED
                ? require('../assets/img/logo-crew-travel.png')
                : require('../assets/img/logo.png')
            }
          />
          <BaseText style={styles.loginText}>{t(tKey('LoginHeader'))}</BaseText>
          <View style={styles.form}>
            <InputForm
              placeholder={t(tKey('EnterEmail'))}
              errorMessage={errorMessages.email}
              value={email}
              onChangeText={(e) => setEmail(e.trim())}
            />
            {forgotPswd && (
              <Modal>
                <ForgotPassword
                  hideModal={() => {
                    setForgotPswd(false);
                    setEmail('');
                    setSso(true);
                  }}
                />
              </Modal>
            )}
            {!isSso && (
              <>
                <InputForm
                  placeholder={t(tKey('EnterPassword'))}
                  value={password}
                  errorMessage={errorMessages.password}
                  secure={true}
                  onChangeText={setPassword}
                />
                <Button
                  type={BUTTON_TYPES.TEXT}
                  onPress={() => setForgotPswd(true)}
                  label={t(tKey('ForgotPassword'))}
                  labelStyle={styles.forgotPswd}
                />
              </>
            )}
            {isSso ? (
              <Button
                style={styles.submitButton}
                label={t('common:Next')}
                labelStyle={CommonStyles.h2}
                onPress={onPressSso}
              />
            ) : (
              <Button
                label={t('common:Login')}
                style={styles.submitButton}
                labelStyle={CommonStyles.h2}
                onPress={onPressLogin}
              />
            )}
          </View>
        </>
      </View>
      {isLoading && <ActivityIndicator color={Color.primary} />}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  logo: {
    resizeMode: 'cover',
    width: '90%',
    height: 60,
    maxWidth: 300,
    ...CommonProperties.mb5,
  },
  crewLogo: {
    resizeMode: 'cover',
    width: '90%',
    height: 100,
    maxWidth: 220,
    ...CommonProperties.mb5,
  },
  body: {
    marginTop: '20%',
    marginBottom: 60,
    alignContent: 'stretch',
    alignItems: 'center',
    ...CommonProperties.p5,
  },
  loginText: {
    textAlign: 'center',
    ...CommonProperties.h2,
    ...CommonProperties.py5,
  },
  form: {
    width: '100%',
    maxWidth: 420,
  },
  submitButton: {
    ...CommonProperties.borderRadius4,
    ...CommonProperties.py3,
    ...CommonProperties.boxShadow,
  },
  forgotPswd: {
    textDecorationLine: 'underline',
  },
});
export default LoginScreen;
